// Value-equality for two Postgres NUMERIC quantities that may arrive in
// different TEXTUAL forms — the crux of the >60-item batch-wedge fix.
//
// `capture_items.quantity` is `numeric('quantity', { precision: 10, scale: 2 })`
// (packages/shared/src/db/schema-capture-items.ts), so the server round-trips a
// stored count as a SCALE-2 string: a local '4' comes back as '4.00', '2.5' as
// '2.50', '0' as '0.00'. The PowerSync client column is `text`
// (hooks/powersync/schema.ts) and holds the UNSCALED value the capture UI wrote.
//
// The same-id last-write-wins re-apply in connector-dispatch-create-item.ts
// fires a PUT /capture/items/:id whenever the server's returned quantity differs
// from the local one. A raw `String(server) !== String(local)` reports
// '4.00' ≠ '4' as DIFFERENT on EVERY idempotent re-send, so that no-op PUT fires
// every time. Under the per-user rate limit (60/60s) each PUT spends a token,
// and a >60-item offline batch — retried all-or-nothing by the PowerSync SDK —
// then wedges forever: the refilled window is burned re-applying already-stored
// rows, the tail never lands, and counts past the window are silently lost
// (the contractual baseline; AGENTS.md "Business criticality").
//
// So compare by decimal VALUE, not text: '4.00' === '4' and '2.50' === '2.5'
// (no needless re-apply) while '3.00' ≠ '4' still diverges (a genuine
// last-write-wins difference re-applies; ADR-003 / Invariant 8).

/**
 * True when `a` and `b` denote the same decimal value, regardless of scale /
 * trailing zeros / leading sign noise. Float-free: each operand is normalised to
 * a canonical decimal string and compared. (`Number()` is exact for
 * numeric(10,2) but would silently lose precision if the column ever widened,
 * and `uom` covers m/m2/m3 so fractional quantities are real — so we never route
 * the comparison through a float.)
 *
 * On an unparseable operand (never expected for a numeric quantity) it falls
 * back to an EXACT textual compare, so an odd value can only ever cause a
 * needless re-apply — never SUPPRESS one and silently drop a count.
 */
export function quantitiesEqual(a: unknown, b: unknown): boolean {
  const ca = canonicalDecimal(a)
  const cb = canonicalDecimal(b)
  if (ca !== null && cb !== null) return ca === cb
  return String(a) === String(b)
}

/**
 * Canonical decimal string for value-comparison, or null when `v` is not a plain
 * decimal literal. Strips a leading '+', redundant leading zeros, and trailing
 * fractional zeros, and folds every zero (incl. '-0', '0.00') to '0'.
 */
function canonicalDecimal(v: unknown): string | null {
  if (typeof v !== 'string' && typeof v !== 'number') return null
  const match = /^([+-]?)(\d+)(?:\.(\d+))?$/.exec(String(v).trim())
  if (match === null) return null
  const sign = match[1] === '-' ? '-' : ''
  const intPart = (match[2] ?? '').replace(/^0+(?=\d)/, '')
  const fracPart = (match[3] ?? '').replace(/0+$/, '')
  if (intPart === '0' && fracPart === '') return '0'
  return fracPart === '' ? `${sign}${intPart}` : `${sign}${intPart}.${fracPart}`
}
